import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import Auth from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import Transactions from "@/pages/Transactions";
import CarbonCalculator from "@/pages/CarbonCalculator";
import Reports from "@/pages/Reports";
import Suppliers from "@/pages/Suppliers";
import CSRDCompliance from "@/pages/CSRDCompliance";
import ChartOfAccounts from "@/pages/ChartOfAccounts";
import UserManagement from "@/pages/UserManagement";
import CompanyManagement from "@/pages/CompanyManagement";
import axios from "axios";

// Protected route wrapper to prevent Platform Owner from accessing regular pages
const ProtectedRoute = ({ children, user, allowedRoles }) => {
  if (!allowedRoles.includes(user?.role)) {
    // Platform owner trying to access regular pages - redirect to admin
    if (user?.role === "platform_owner") {
      return <Navigate to="/admin/companies" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up axios interceptor to automatically add auth token
    const setupAxiosInterceptor = () => {
      axios.interceptors.request.use(
        (config) => {
          const token = localStorage.getItem("token");
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
          return config;
        },
        (error) => {
          return Promise.reject(error);
        }
      );
    };

    setupAxiosInterceptor();

    // Check for existing session
    const token = localStorage.getItem("token");
    const savedUser = localStorage.getItem("user");
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f2f7f4]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a2e22]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#f2f7f4]">
      <div className="noise-overlay" />
      <BrowserRouter>
        <Routes>
          <Route
            path="/auth"
            element={
              user ? (
                user.role === "platform_owner" ? 
                  <Navigate to="/admin/companies" replace /> : 
                  <Navigate to="/dashboard" replace />
              ) : (
                <Auth onLogin={handleLogin} />
              )
            }
          />
          <Route
            path="/"
            element={
              user ? <Layout user={user} onLogout={handleLogout} /> : <Navigate to="/auth" replace />
            }
          >
            <Route 
              index 
              element={
                user?.role === "platform_owner" 
                  ? <Navigate to="/admin/companies" replace /> 
                  : <Navigate to="/dashboard" replace />
              } 
            />
            
            {/* Regular User Routes - Block Platform Owner */}
            <Route 
              path="dashboard" 
              element={
                <ProtectedRoute user={user} allowedRoles={["company_admin", "user"]}>
                  <Dashboard />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="transactions" 
              element={
                <ProtectedRoute user={user} allowedRoles={["company_admin", "user"]}>
                  <Transactions />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="accounts" 
              element={
                <ProtectedRoute user={user} allowedRoles={["company_admin", "user"]}>
                  <ChartOfAccounts />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="calculator" 
              element={
                <ProtectedRoute user={user} allowedRoles={["company_admin", "user"]}>
                  <CarbonCalculator />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="reports" 
              element={
                <ProtectedRoute user={user} allowedRoles={["company_admin", "user"]}>
                  <Reports />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="suppliers" 
              element={
                <ProtectedRoute user={user} allowedRoles={["company_admin", "user"]}>
                  <Suppliers />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="compliance" 
              element={
                <ProtectedRoute user={user} allowedRoles={["company_admin", "user"]}>
                  <CSRDCompliance />
                </ProtectedRoute>
              } 
            />
            
            {/* Admin Routes */}
            <Route 
              path="admin/users" 
              element={
                <ProtectedRoute user={user} allowedRoles={["platform_owner", "company_admin"]}>
                  <UserManagement currentUser={user} />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="admin/companies" 
              element={
                <ProtectedRoute user={user} allowedRoles={["platform_owner"]}>
                  <CompanyManagement currentUser={user} />
                </ProtectedRoute>
              } 
            />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
